import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getDisplayTextLabelText, logger } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { getKeyCodeForEvent, KeyCode } from '../../../../utils/key-code';
import { Button } from '../../../button/button';
import { ButtonColor } from '../../../button/types/color';
import { Dropdown } from '../dropdown/dropdown';
import { defaultNumberInputProps } from './types/props';
import { NumberInputType } from './types/type';
const b = bemClassBuilder('v-number-input');
const bInput = bemClassBuilder('v-input');
/**
 * Renders the number input component
 */
export const NumberInput = observer(props => {
    var _a, _b, _c, _d, _e;
    const { id, name, disabled, helpTextId, label, className, style } = props;
    const value = (_a = props.value) !== null && _a !== void 0 ? _a : 0;
    const max = (_b = props.max) !== null && _b !== void 0 ? _b : defaultNumberInputProps.max;
    const min = (_c = props.min) !== null && _c !== void 0 ? _c : defaultNumberInputProps.min;
    const step = (_d = props.step) !== null && _d !== void 0 ? _d : defaultNumberInputProps.step;
    let type = (_e = props.type) !== null && _e !== void 0 ? _e : defaultNumberInputProps.type;
    const icons = Object.assign(Object.assign({}, defaultNumberInputProps.icons), props.icons);
    // Ensure that a safe number of options are rendered for the Dropdown style
    if (type === NumberInputType.Dropdown && max - min > 100) {
        type = NumberInputType.Basic;
        logger.warn('Min and max outside safe range for dropdown. Defaulting to basic.');
    }
    const wrapperClass = b.block({ type }, className);
    if (max < min) {
        throw new RangeError(`The min value: ${min} must be lower than the max value: ${max}`);
    }
    if (step <= 0)
        throw new RangeError('The step value must be greater than 0');
    if (value < min || value > max) {
        throw new RangeError(`The value: ${value} must be within the min and max range of ${min} & ${max} respectively.`);
    }
    const getInputClass = (elementClass, formElementStyle) => {
        return `${bInput.block({ style: formElementStyle })} ${elementClass}`;
    };
    const getDropdownOptions = () => {
        const options = new Array();
        for (let i = min; i <= max; i += step) {
            options.push({ value: i, text: i.toString() });
        }
        return options;
    };
    const handleChangeEvent = event => onChange(event.target.value);
    const onChange = (changedValue) => {
        var _a;
        let newValue = Number(changedValue);
        if (isNaN(newValue))
            newValue = min;
        // TODO:: Investigate auto-correcting to minimum. #312108
        if (newValue < min)
            newValue = min;
        if (newValue > max)
            newValue = max;
        (_a = props.onChangeValue) === null || _a === void 0 ? void 0 : _a.call(props, newValue);
    };
    const handleKeyDown = event => {
        const keyCode = getKeyCodeForEvent(event);
        switch (keyCode) {
            case KeyCode.Plus:
            case KeyCode.UpArrow:
                event.preventDefault();
                incrementValue();
                break;
            case KeyCode.Minus:
            case KeyCode.DownArrow:
                event.preventDefault();
                decrementValue();
                break;
            default:
                break;
        }
    };
    const incrementValue = () => onChange(value + step);
    const handleIncrement = event => {
        event.preventDefault(); // Prevent double tap zoom on mobile device
        incrementValue();
    };
    const decrementValue = () => onChange(value - step);
    const handleDecrement = event => {
        event.preventDefault(); // Prevent double tap zoom on mobile device
        decrementValue();
    };
    if (type === NumberInputType.Dropdown) {
        return (_jsx("div", Object.assign({ className: wrapperClass }, { children: _jsx(Dropdown, { id: id, value: value, buttonIconName: icons.dropdownButtonIconName, disabled: disabled, onChangeValue: onChange, options: getDropdownOptions(), helpTextId: helpTextId, label: label, enableSearch: false }) })));
    }
    return (_jsxs("div", Object.assign({ className: wrapperClass }, { children: [type === NumberInputType.Spinner && (_jsx(Button, { className: b.element('button', { minus: true }), color: ButtonColor.Tertiary, onClick: handleDecrement, disabled: disabled || value === min, tabIndex: -1, iconName: icons.minusButtonIconName, iconOnly: true })), _jsx("input", { id: id, className: getInputClass(b.element('input'), style), inputMode: "decimal", type: "number", 
                /*
                 * Possibly a react bug where the value in not getting re-rendered as react sees
                 * no difference between the old and the new value as a number.
                 * i.e. 09 and 9 are the same. Therefore, it doesn't update the value property.
                 */
                value: value.toString(), onChange: handleChangeEvent, onKeyDown: handleKeyDown, name: name, disabled: disabled, "aria-describedby": helpTextId, "aria-label": label ? getDisplayTextLabelText(label) : undefined, min: min, max: max, step: step }), type === NumberInputType.Spinner && (_jsx(Button, { className: b.element('button', { plus: true }), color: ButtonColor.Tertiary, onClick: handleIncrement, disabled: disabled || value === max, tabIndex: -1, iconName: icons.plusButtonIconName, iconOnly: true }))] })));
});
NumberInput.displayName = 'NumberInput';
